import React, { useState } from 'react';
import TemplateVariablesHelp from './TemplateVariablesHelp';
import {
  CreateCustomCommandInput,
  validateCustomCommand,
  COMMAND_LIMITS,
  processTemplateVariables
} from '../lib/customCommands';
import { supabase } from '../lib/supabase';

interface CustomCommandFormProps {
  onSubmit: (command: CreateCustomCommandInput) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  initialData?: Partial<CreateCustomCommandInput>;
}

const CustomCommandForm: React.FC<CustomCommandFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
  initialData
}) => {
  const [formData, setFormData] = useState<CreateCustomCommandInput>({
    trigger_command: initialData?.trigger_command || '',
    response_message: initialData?.response_message || '',
    enabled: initialData?.enabled !== false,
    case_sensitive: initialData?.case_sensitive === true,
    requires_prefix: true, // Always required - hidden from UI
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [showTemplateHelp, setShowTemplateHelp] = useState(false);
  const [previewResponse, setPreviewResponse] = useState<string>('');
  const [mentionWord, setMentionWord] = useState<string>('dork');

  // Fetch mention_word on component mount
  React.useEffect(() => {
    const fetchMentionWord = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: rumbleSettings } = await supabase
            .from('user_rumble_settings')
            .select('mention_word')
            .eq('user_id', user.id)
            .single();

          if (rumbleSettings?.mention_word) {
            setMentionWord(rumbleSettings.mention_word);
          }
        }
      } catch (error) {
        console.error('Failed to fetch mention word:', error);
      }
    };

    fetchMentionWord();
  }, []);

  // Update form data when initialData changes (for editing)
  React.useEffect(() => {
    if (initialData) {
      setFormData({
        trigger_command: initialData.trigger_command || '',
        response_message: initialData.response_message || '',
        enabled: initialData.enabled !== false,
        case_sensitive: initialData.case_sensitive === true,
        requires_prefix: true,
      });
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateCustomCommand(formData, mentionWord);
    setErrors(validationErrors);
    
    if (validationErrors.length > 0) {
      return;
    }
    
    try {
      await onSubmit(formData);
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Failed to save command']);
    }
  };

  const handleTriggerChange = (value: string) => {
    // Always auto-add ! prefix if not present
    let processedValue = value;
    if (!value.startsWith('!') && value.length > 0) {
      processedValue = '!' + value;
    }
    setFormData({ ...formData, trigger_command: processedValue });
  };

  // Update preview when response changes
  React.useEffect(() => {
    if (formData.response_message) {
      const preview = processTemplateVariables(formData.response_message, {
        senderUsername: '@viewer123',
        commandTrigger: formData.trigger_command,
        userCount: 42,
        uptime: '2h 15m'
      });
      setPreviewResponse(preview);
    } else {
      setPreviewResponse('');
    }
  }, [formData.response_message, formData.trigger_command]);

  const triggerLength = (formData.trigger_command || '').length;
  const responseLength = (formData.response_message || '').length;
  const isTriggerTooLong = triggerLength > COMMAND_LIMITS.MAX_TRIGGER_LENGTH;
  const isResponseTooLong = responseLength > COMMAND_LIMITS.MAX_RESPONSE_LENGTH;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-black border-2 border-white p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-white">
            {initialData ? 'Edit Custom Command' : 'Add Custom Command'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-white transition-colors"
            disabled={loading}
          >
            <i className="fa-solid fa-times text-xl"></i>
          </button>
        </div>

        {errors.length > 0 && (
          <div className="mb-4 p-3 border-2 border-red-500 text-red-400">
            <ul className="list-disc list-inside">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Trigger Command */}
          <div>
            <label htmlFor="trigger_command" className="paint-label">
              Trigger Command *
            </label>
            <input
              type="text"
              id="trigger_command"
              value={formData.trigger_command}
              onChange={(e) => handleTriggerChange(e.target.value)}
              className={`paint-input ${isTriggerTooLong ? 'border-red-500' : ''}`}
              placeholder="hello"
              maxLength={COMMAND_LIMITS.MAX_TRIGGER_LENGTH + 5}
              disabled={loading}
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={isTriggerTooLong ? 'text-red-400' : 'text-gray-400'}>
                {triggerLength}/{COMMAND_LIMITS.MAX_TRIGGER_LENGTH} characters
              </span>
              {isTriggerTooLong && (
                <span className="text-red-400">Command too long</span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1">
              Chatters will type this to trigger the command. Letters, numbers, hyphens, and underscores only.
            </p>
          </div>

          {/* Response Message */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label htmlFor="response_message" className="paint-label">
                Response Message *
              </label>
              <button
                type="button"
                onClick={() => setShowTemplateHelp(true)}
                className="text-blue-400 hover:text-blue-300 transition-colors text-sm"
                title="View template variables"
              >
                <i className="fa-solid fa-question-circle mr-1"></i>
                Template Variables
              </button>
            </div>
            <textarea
              id="response_message"
              value={formData.response_message}
              onChange={(e) => setFormData({ ...formData, response_message: e.target.value })}
              className={`paint-input h-24 resize-none ${isResponseTooLong ? 'border-red-500' : ''}`}
              placeholder="Hello {senderUsername}! Welcome to the stream!"
              maxLength={COMMAND_LIMITS.MAX_RESPONSE_LENGTH + 50}
              disabled={loading}
            />
            <div className="flex justify-between text-xs mt-1">
              <span className={isResponseTooLong ? 'text-red-400' : 'text-gray-400'}>
                {responseLength}/{COMMAND_LIMITS.MAX_RESPONSE_LENGTH} characters
              </span>
              {isResponseTooLong && (
                <span className="text-red-400">Response too long</span>
              )}
            </div>

            {/* Preview */}
            {previewResponse && (
              <div className="mt-2 p-2 bg-gray-900 border border-gray-600 rounded">
                <p className="text-xs text-gray-400 mb-1">Preview:</p>
                <p className="text-gray-300 text-sm">"{previewResponse}"</p>
              </div>
            )}
          </div>

          {/* Command Settings */}
          <div className="space-y-3">
            <h3 className="text-lg font-medium text-white">Command Settings</h3>

            <div>
              <label className="paint-label inline-flex items-center justify-between">
                <span>Case sensitive:</span>
                <label className="toggle-switch ml-4">
                  <input 
                    type="checkbox" 
                    checked={formData.case_sensitive}
                    onChange={(e) => setFormData({ ...formData, case_sensitive: e.target.checked })}
                    disabled={loading}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </label>
              <p className="text-xs text-gray-400 mt-1">
                If disabled, "HELLO", "hello", and "Hello" will all trigger the command
              </p>
            </div>

            <div>
              <label className="paint-label inline-flex items-center justify-between">
                <span>Enable command:</span>
                <label className="toggle-switch ml-4">
                  <input 
                    type="checkbox" 
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    disabled={loading}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </label>
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="paint-button paint-button-sm bg-gray-700 hover:bg-gray-600"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="paint-button paint-button-sm"
              disabled={loading || isTriggerTooLong || isResponseTooLong}
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-save mr-2"></i>
                  {initialData ? 'Update Command' : 'Add Command'}
                </>
              )}
            </button>
          </div>
        </form>

        {/* Template Variables Help Modal */}
        <TemplateVariablesHelp 
          isOpen={showTemplateHelp}
          onClose={() => setShowTemplateHelp(false)}
        />
      </div>
    </div>
  );
};

export default CustomCommandForm;